import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { request } from '@/lib/api.js';
import { useToast } from '@/components/Toast.js';
import { useFmt } from '@/lib/useFmt.js';
import { Chip, GlassButton, PageHeader, Pagination, useConfirm } from '@/components/ui/index.js';
import type { GrantSubscriptionResponse, UserListItem, UserListResponse } from '@/lib/types.js';

const TIER_LABEL = {
  free: '免费',
  standard: '标准',
  pro: 'Pro',
  premium: 'Premium',
} as const;

const PAGE_SIZE = 50;

export function UsersPage(): JSX.Element {
  const confirm = useConfirm();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [granting, setGranting] = useState<string | null>(null);
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('all');
  const { fmtDate, fmtDateTime } = useFmt();

  const load = useCallback(async (searchValue: string, offsetValue: number): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offsetValue),
      });
      if (searchValue !== '') params.set('search', searchValue);
      const res = await request<UserListResponse>(`/admin/users?${params.toString()}`);
      setUsers(res.users);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(search, offset);
  }, [search, offset, load]);

  async function handleGrant(
    user: UserListItem,
    plan: 'monthly' | 'yearly',
  ): Promise<void> {
    // Audit B5: tier-changing actions require confirmation.
    const label = plan === 'monthly' ? '一个月' : '一年';
    const who = user.email ?? user.phone ?? user.id;
    const ok = await confirm({
      title: '增加会员',
      message: `确认给 ${who} 增加${label}会员？此操作会立即生效。`,
      confirmLabel: '确认增加',
      tone: 'warning',
    });
    if (!ok) return;

    setGranting(user.id);
    try {
      await request<GrantSubscriptionResponse>(
        `/admin/users/${user.id}/grant_subscription`,
        {
          method: 'POST',
          body: { plan },
        },
      );
      useToast.getState().show('success', `已给 ${who} 添加${label}会员`);
      await load(search, offset);
    } catch (err) {
      useToast.getState().show(
        'error',
        err instanceof Error ? err.message : '续期失败',
      );
    } finally {
      setGranting(null);
    }
  }

  // 客户端快速筛选 — admin 用户量小（< 1000），不值得为这种 UI 切换
  // 给后端加 query param。如果之后 user 量大需要服务端过滤再迁。
  const filteredUsers = users.filter((u) => applyQuickFilter(u, quickFilter));

  return (
    <div>
      <PageHeader
        title="用户"
        subtitle={`所有注册账号，含 web / desktop / 闲鱼开户。总共 ${total} 人。`}
      />

      {/* 快速筛选 chips */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs text-slate-500 mr-1">快速筛选</span>
        {QUICK_FILTERS.map((f) => {
          const matchCount = users.filter((u) => applyQuickFilter(u, f.key)).length;
          return (
            <Chip
              key={f.key}
              active={quickFilter === f.key}
              onClick={(): void => setQuickFilter(f.key)}
            >
              {f.label}
              <span className="ml-1 opacity-60">{matchCount}</span>
            </Chip>
          );
        })}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearch(searchInput);
              setOffset(0);
            }
          }}
          placeholder="按邮箱 / 手机号 / 用户 ID 搜索"
          className="h-10 px-3 rounded-full bg-white/[0.03] border border-white/10 text-slate-200 placeholder:text-slate-500 focus:border-shark-500 focus:outline-none w-full sm:w-80 min-w-0 text-sm"
        />
        <GlassButton
          size="md"
          variant="primary"
          onClick={() => {
            setSearch(searchInput);
            setOffset(0);
          }}
        >
          搜索
        </GlassButton>
        {search !== '' && (
          <GlassButton
            size="md"
            variant="ghost"
            onClick={() => {
              setSearch('');
              setSearchInput('');
              setOffset(0);
            }}
          >
            清除
          </GlassButton>
        )}
        <span className="ml-auto text-sm text-slate-400">
          {quickFilter === 'all' ? (
            <>共 {total} 个用户</>
          ) : (
            <>当前筛选 {filteredUsers.length} / 共 {total}</>
          )}
        </span>
      </div>

      {error !== null && (
        <div className="text-sm text-rose-300 bg-rose-900/30 border border-rose-700/50 rounded-lg px-3 py-2 mb-4">
          {error}
        </div>
      )}

      <div className="mat-matte rounded-xl overflow-hidden">
       <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-white/[0.04]">
            <tr>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">邮箱 / 手机</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">会员</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">测评</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">到期</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">注册时间</th>
              <th className="px-4 py-3 text-left text-slate-300 font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                  加载中…
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                  {users.length === 0 ? '暂无用户' : '当前筛选下没有匹配用户'}
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr key={u.id} className="border-t border-white/[0.08] hover:bg-white/[0.04]">
                  <td className="px-4 py-3">
                    <Link to={`/users/${u.id}`} className="block">
                      <div className="text-shark-300 hover:text-shark-200">
                        {u.email ?? <span className="text-slate-500">—</span>}
                      </div>
                      {u.phone !== null && (
                        <div className="text-xs text-slate-500">{u.phone}</div>
                      )}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        u.tier === 'free'
                          ? 'text-slate-300'
                          : 'text-shark-300 font-semibold'
                      }
                    >
                      {TIER_LABEL[u.tier]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <AssessmentCell
                      count={u.assessment_count}
                      bestRank={u.best_rank}
                      latestComposite={u.latest_composite}
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-300">
                    {u.expires_at !== null
                      ? fmtDate(u.expires_at)
                      : <span className="text-slate-500">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    {fmtDateTime(u.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <GlassButton
                        size="sm"
                        variant="ghost"
                        onClick={(): void => { void handleGrant(u, 'monthly'); }}
                        disabled={granting === u.id}
                      >
                        +1 月
                      </GlassButton>
                      <GlassButton
                        size="sm"
                        variant="ghost"
                        onClick={(): void => { void handleGrant(u, 'yearly'); }}
                        disabled={granting === u.id}
                      >
                        +1 年
                      </GlassButton>
                      <GlassButton size="sm" variant="primary" to={`/users/${u.id}`}>
                        详情
                      </GlassButton>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
       </div>
      </div>

      <Pagination
        offset={offset}
        total={total}
        pageSize={PAGE_SIZE}
        onChange={setOffset}
      />
    </div>
  );
}

type QuickFilter = 'all' | 'standard' | 'free' | 'never_logged_in' | 'no_assessment';

const QUICK_FILTERS: readonly { key: QuickFilter; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'standard', label: '会员' },
  { key: 'free', label: '免费' },
  { key: 'never_logged_in', label: '未登录' },
  { key: 'no_assessment', label: '未测评' },
] as const;

function applyQuickFilter(u: UserListItem, f: QuickFilter): boolean {
  if (f === 'all') return true;
  if (f === 'standard') return u.tier !== 'free';
  if (f === 'free') return u.tier === 'free';
  if (f === 'never_logged_in') return u.last_login_at === null;
  if (f === 'no_assessment') return u.assessment_count === 0;
  return true;
}

const RANK_TONE: Record<'S' | 'A' | 'B' | 'C' | 'D', string> = {
  S: 'text-amber-300',
  A: 'text-rose-300',
  B: 'text-indigo-300',
  C: 'text-emerald-300',
  D: 'text-slate-400',
};

function AssessmentCell({
  count,
  bestRank,
  latestComposite,
}: {
  count: number;
  bestRank: 'S' | 'A' | 'B' | 'C' | 'D' | null;
  latestComposite: number | null;
}): JSX.Element {
  if (count === 0) {
    return <span className="text-slate-600 text-xs">未测评</span>;
  }
  return (
    <div className="flex items-baseline gap-1.5 text-xs">
      <span className="text-slate-300 font-medium">{count} 次</span>
      {bestRank !== null && (
        <span className={`font-semibold ${RANK_TONE[bestRank]}`}>{bestRank}</span>
      )}
      {latestComposite !== null && (
        <span className="text-slate-500">{latestComposite.toFixed(0)} 分</span>
      )}
    </div>
  );
}
